export class AdminApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly details?: unknown) {
    super(code);
    this.name = "AdminApiError";
  }
}

export const adminApiOrigin = import.meta.env.VITE_API_ORIGIN ?? (import.meta.env.DEV ? "http://127.0.0.1:8787" : window.location.origin);

export async function adminApi<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...requestInit } = init;
  const headers = new Headers(requestInit.headers);
  let body = requestInit.body;
  if (json !== undefined) {
    headers.set("content-type", "application/json");
    body = JSON.stringify(json);
  }
  const options: RequestInit = { ...requestInit, credentials:"include", headers };
  if (body !== undefined) options.body = body;
  const response = await fetch(adminApiOrigin + path, options);
  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await response.json() as unknown : await response.text();
  if (!response.ok) {
    const record = typeof payload === "object" && payload !== null ? payload as Record<string,unknown> : {};
    throw new AdminApiError(response.status,String(record.error ?? "REQUEST_FAILED"),payload);
  }
  return payload as T;
}

export const adminErrorMessage = (error: unknown) => {
  if (error instanceof AdminApiError) {
    const map: Record<string,string> = {
      OWNER_AUTHORITY_REQUIRED:"Owner authority is required for this operation.",
      AUTH_REQUIRED:"The owner session has expired.",
      READY_PRODUCT_FILE_REQUIRED:"Upload a ready paid product file before publishing.",
      ARCHIVE_BEFORE_DELETE:"Archive the item before deleting it.",
      ACTIVE_ENTITLEMENTS_EXIST:"This product has active customer entitlements and cannot be deleted.",
      SLUG_ALREADY_EXISTS:"That URL slug is already in use.",
      INVALID_ORDER_TRANSITION:"This order state no longer permits that decision.",
      ORDER_CHANGED_RETRY:"The order changed during review. Refresh and review again.",
      SHA256_MISMATCH:"The uploaded file hash does not match the supplied SHA-256.",
      FILE_TOO_LARGE:"The file exceeds the current 50 MB direct-upload ceiling.",
      INVALID_MODERATION_TRANSITION:"This contribution state no longer permits that moderation action.",
      NOT_FOUND_OR_ALREADY_REVIEWED:"This report was already reviewed or no longer exists.",
      COMMUNITY_DISABLED:"Community participation is currently disabled.",
      ARCHIVE_SERVICE_BEFORE_EDIT:"Archive the service offering before editing it.",
      SERVICE_ACTIVE_BOOKINGS_EXIST:"This service still has active bookings and cannot be deleted.",
      BOOKING_REVIEW_STATE_INVALID:"This booking can no longer enter review.",
      BOOKING_REJECT_STATE_INVALID:"This booking can no longer be rejected from its current state.",
      BOOKING_QUOTE_STATE_INVALID:"A quote cannot be issued from the current booking state.",
      BOOKING_QUOTE_EXPIRY_INVALID:"Choose a quote expiry in the future.",
      BOOKING_SCHEDULE_STATE_INVALID:"Only an accepted booking can be scheduled.",
      BOOKING_SCHEDULE_CONFLICT:"The selected schedule conflicts with another confirmed booking.",
      SCHEDULE_SLOT_ALIGNMENT_REQUIRED:"Schedule start and end must align to 15-minute boundaries.",
      SCHEDULE_MUST_BE_FUTURE:"The confirmed schedule must be in the future.",
      SCHEDULE_WINDOW_INVALID:"Choose a valid schedule start and end.",
      BOOKING_STATE_TRANSITION_INVALID:"This booking state no longer permits that action.",
      ARCHIVE_ANNOUNCEMENT_BEFORE_EDIT:"Archive the published announcement before editing it.",
      INVALID_INBOX_SOURCE:"The requested Inbox item source is invalid.",
      INVALID_INBOX_STATUS:"The requested Inbox filter is invalid.",
    };
    return map[error.code] ?? error.code.replaceAll("_"," ").toLowerCase();
  }
  return error instanceof Error ? error.message : "An unexpected operation error occurred.";
};
